import { useRoleCheck } from '@/hooks/useRoleCheck';
import { Permission } from '@/types/rolesTypes';
import { buildPermissionMap, can, hasAnyAccess, PermissionMap } from '@/utils/managePermissions';
import { createContext, useContext, useMemo, ReactNode } from 'react';

type AccessType =
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'download'
  | 'detailedView'
  | 'manageAccount'
  | 'manageRequest';

type PrivilegeItem = {
  updatedAccess?: Partial<Record<AccessType, boolean>>;
  privilege?: {
    permissions?: string;
  };
};

interface PermissionContextValue {
  permissionMap: PermissionMap;
  isOwner: boolean;
  isAgent: boolean;
  hasAnyAccess: (key: string) => boolean;
  can: (key: string, action: string) => boolean;
  hasModuleAccess: (moduleName: string, accessType: AccessType) => boolean;
  user: Permission;
  isLoading: boolean;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export function PermissionProvider({
  user,
  children,
  isLoading,
}: {
  user: Permission;
  children: ReactNode;
  isLoading: boolean;
}) {
  const { isOwner, isAgent } = useRoleCheck();

  const permissionMap = useMemo(() => buildPermissionMap(user), [user]);

  const allowedActions: PrivilegeItem[] = user?.role?.rolePrivileges ?? [];

  const hasModuleAccess = (moduleName: string, accessType: AccessType): boolean => {
    if (isOwner || isAgent) return true;

    const moduleData = allowedActions.find((item) => item?.privilege?.permissions === moduleName);

    return moduleData?.updatedAccess?.[accessType] === true;
  };

  const value = useMemo(
    () => ({
      permissionMap,
      isOwner,
      isAgent,
      user,
      hasAnyAccess: (key: string) => isOwner || hasAnyAccess(permissionMap, key),
      can: (key: string, action: string) => isOwner || can(permissionMap, key, action),
      hasModuleAccess,
      isLoading,
    }),
    [permissionMap, user, isOwner, isAgent, allowedActions]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() {
  const ctx = useContext(PermissionContext);

  if (!ctx) {
    throw new Error('usePermissions must be used inside PermissionProvider');
  }

  return ctx;
}
